import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App, { preloadRoute } from './App';
import './index.css';
import { captureAttribution } from './lib/attribution';

const root = document.getElementById('root')!;

// Hydrate when content was prerendered into #root; otherwise client-render.
// The current route's chunk is preloaded BEFORE hydrating so React.lazy
// resolves from module cache within a microtask — without this, hydration
// starts while the chunk is still loading, the Suspense fallback commits
// against the prerendered content, and React falls back to client rendering
// for the entire root (minified error #418).
// Hosting serves the same prerendered file regardless of the query string,
// but helpdesk pages render differently for ?page=N (cumulative listing) and
// ?embed=1 (no chrome) — that markup can never match, so client-render those.
function prerenderMatchesUrl(): boolean {
  if (!window.location.pathname.startsWith('/p/helpdesk')) return true;
  const qs = new URLSearchParams(window.location.search);
  return !qs.has('page') && !qs.has('embed');
}

function start() {
  // Record where this visitor came from (any page, first touch kept 90 days).
  captureAttribution();

  if (root.hasChildNodes() && prerenderMatchesUrl()) {
    // A failed preload (e.g. network error) must not block hydration —
    // React will client-render the route as before.
    preloadRoute(window.location.pathname)
      .catch(() => undefined)
      .finally(() => {
        hydrateRoot(
          root,
          <StrictMode>
            <App />
          </StrictMode>,
        );
      });
  } else {
    createRoot(root).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  }
}

start();
